// @ts-strict-ignore
/**
 * @owner @Appboy/operator
 */

import * as Sentry from "@sentry/browser";
import type { Channel } from "pusher-js";
import Pusher from "pusher-js";

import type {
  PusherChannelAndEventSubscriptionProps,
  PusherChannelOnlySubscriptionProps,
  PusherSubscriptionStatus,
} from "@lib/pusher";

// eslint-disable-next-line local-eslint-rules/enforce-namespace-modularity -- monorepo migration will move this
import { getNowTimestamp } from "@services/getNowTimestamp/getNowTimestamp";

import type { PusherConnectionDiagnostics, PusherConnectionStats } from "./types";

const SUBSCRIPTION_PENDING: PusherSubscriptionStatus = "pending";

// No-op Pusher stub for when Pusher is disabled
const NO_OP_PUSHER = {
  subscribe: () => ({}),
  channel: () => ({ bind: () => {}, unbind: () => {}, callbacks: new Map() }),
  connection: {
    bind: () => {},
    unbind: () => {},
    state: "initialized",
  },
} as any;

export class BrazePusher {
  public pusher: Pusher;
  public selfChannelSubscriptionStatus: PusherSubscriptionStatus;
  public globalChannelSubscriptionStatus: PusherSubscriptionStatus;
  public onSubscribedSelfChannelFns: ((...args) => void)[];
  public localChannelNames: string[];
  public initialGlobalSubscriptionsRegistered: boolean;
  public initialCompanySubscriptionsRegistered: boolean;
  public initialDeveloperSubscriptionsRegistered: boolean;
  public developerSelfChannel: Channel;

  private readonly enabled: boolean;
  private connectionStats: PusherConnectionStats;

  constructor(pusherKey: string, pusherCluster: string, isDevOrStaging: boolean, enabled = true) {
    this.enabled = enabled;
    this.pusher = enabled
      ? new Pusher(pusherKey, {
          cluster: pusherCluster,
        })
      : NO_OP_PUSHER;

    if (enabled && isDevOrStaging) {
      Pusher.log = (message) => {
        console.log(message);
        Sentry.addBreadcrumb({
          category: "pusher",
          message,
          level: "info",
        });
      };
    }

    this.selfChannelSubscriptionStatus = SUBSCRIPTION_PENDING;
    this.globalChannelSubscriptionStatus = SUBSCRIPTION_PENDING;
    this.onSubscribedSelfChannelFns = [];
    this.localChannelNames = [];
    this.initialGlobalSubscriptionsRegistered = false;
    this.initialCompanySubscriptionsRegistered = false;
    this.initialDeveloperSubscriptionsRegistered = false;

    this.connectionStats = {
      connectCount: 0,
      disconnectCount: 0,
      reconnectCount: 0,
      errorCount: 0,
      lastConnectedAt: null,
      lastDisconnectedAt: null,
      lastStateChangeAt: null,
      lastErrorAt: null,
      lastErrorCode: null,
      lastErrorMessage: null,
    };

    this.bindConnectionDiagnostics();
  }

  private bindConnectionDiagnostics = () => {
    // Bind even when disabled — the NO_OP stub's `bind` is a no-op, so this
    // is safe and keeps the call site branch-free.
    this.pusher.connection.bind("state_change", (payload: unknown) => {
      if (typeof payload !== "object" || payload === null) {
        return;
      }
      const { previous, current } = payload as { previous?: unknown; current?: unknown };
      if (typeof current !== "string") {
        return;
      }

      const now = getNowTimestamp();
      this.connectionStats.lastStateChangeAt = now;

      if (current === "connected") {
        // The first connect is the initial bootstrap; every subsequent
        // connect is a reconnect.
        if (this.connectionStats.connectCount >= 1) {
          this.connectionStats.reconnectCount += 1;
        }
        this.connectionStats.connectCount += 1;
        this.connectionStats.lastConnectedAt = now;
      }

      if (typeof previous === "string" && previous === "connected" && current !== "connected") {
        this.connectionStats.disconnectCount += 1;
        this.connectionStats.lastDisconnectedAt = now;
      }
    });

    this.pusher.connection.bind("error", (err: unknown) => {
      this.connectionStats.errorCount += 1;
      this.connectionStats.lastErrorAt = getNowTimestamp();

      const readProp = (value: unknown, key: string): unknown => {
        if (typeof value !== "object" || value === null) {
          return undefined;
        }
        return (value as Record<string, unknown>)[key];
      };
      const data = readProp(readProp(err, "error"), "data");
      const code = readProp(data, "code");
      const message = readProp(data, "message");

      this.connectionStats.lastErrorCode = typeof code === "number" ? code : null;
      this.connectionStats.lastErrorMessage = typeof message === "string" ? message : null;
    });
  };

  /**
   * Returns a snapshot of Pusher connection state for diagnostic logging.
   * Mirrors `DustProvider.getConnectionDiagnostics` so both transports can be
   * attached to the same error payload. `state` / `socketId` are null when
   * the pusher-js client is the NO_OP stub (Pusher disabled via config/FF).
   */
  getConnectionDiagnostics = (): PusherConnectionDiagnostics => {
    const connection = this.pusher.connection as { state?: string; socket_id?: string };
    const state = this.enabled ? (connection?.state ?? null) : null;
    const socketId = this.enabled ? (connection?.socket_id ?? null) : null;

    return {
      enabled: this.enabled,
      state,
      connected: state === "connected",
      socketId,
      ...this.connectionStats,
      subscribedChannelCount: this.localChannelNames.length,
      selfChannelSubscriptionStatus: this.selfChannelSubscriptionStatus,
      globalChannelSubscriptionStatus: this.globalChannelSubscriptionStatus,
      isOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
    };
  };

  /**
   * Subscribes to a Pusher channel (if not already subscribed) and binds a callback
   * to a given event received on that channel.
   * @param channelName the name of the Pusher channel to subscribe to
   * @param callback (Optional) the function to call when the event is received
   * @param eventName (Optional) the name of the event to subscribe to on the given channel
   */
  subscribeToChannel = ({
    channelName,
    callback = undefined,
    eventName = undefined,
  }: PusherChannelOnlySubscriptionProps | PusherChannelAndEventSubscriptionProps) => {
    const existingChannelId = this.localChannelNames.indexOf(channelName);
    if (existingChannelId === -1) {
      this.localChannelNames.push(channelName);
      this.pusher.subscribe(channelName);
    }

    if (eventName && callback) {
      const channel = this.pusher.channel(channelName);

      // If attempting to re-bind a callback to a given channel & event pair then
      // we unbind the current callback first so that the most recent subscription wins
      if (channel.callbacks.get(eventName)?.length != 0) {
        channel.unbind(eventName);
      }
      channel.bind(eventName, callback);
    }
  };

  /**
   * @param channelName the channel containing the event to unsubscribe from
   * @param callback (Optional) the function to unbind from the event
   * @param eventName (Optional) the name of the event to unbind from the given channel
   * If both `callback` and `eventName` are not provided, all events on the channel will be unbound
   * @returns false if the channel was not found, true otherwise
   */
  unsubscribeFromChannel = ({ channelName, eventName, callback }: PusherChannelAndEventSubscriptionProps) => {
    const existingChannelId = this.localChannelNames.indexOf(channelName);
    if (existingChannelId === -1) {
      return false;
    }

    // Although the method is called `unsubscribeFromChannel`, in practice we do not want to be
    // subscribing and unsubscribing from the same channel multiple times so instead we just unbind
    this.pusher.channel(channelName).unbind(eventName, callback);
    // Remove the channel from the local list of subscriptions
    this.localChannelNames.splice(existingChannelId, 1);

    return true;
  };

  onSuccessSubscribedToSelfChannel = (fn: (...args) => void) => {
    return this.onSubscribedSelfChannelFns.push(fn);
  };
}
